import React from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { Icon } from '../Icon/Icon';
import type { IconName } from '../Icon/icon.types';

import type { RawButtonProps } from './RawButton';
import { RawButton } from './RawButton';

export enum IconButtonSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export type IconButtonProps = {
  className?: RawButtonProps['className'];
  disabled?: RawButtonProps['disabled'];
  /** @see RawButtonProps.download */
  download?: RawButtonProps['download'];
  /** @see RawButtonProps.softDisabled */
  softDisabled?: RawButtonProps['softDisabled'];
  /**
   * Sits the button on top of what it acts on rather than beside it - a cue over a transcript, a
   * control over a map. It takes a face of its own, because a transparent one would have that
   * content reading through it, and stops reacting to the pointer, because a button that lights up
   * reads as acting on whatever it is sitting over.
   */
  floating?: boolean;
  icon: IconName;
  id?: RawButtonProps['id'];
  inverse?: RawButtonProps['inverse'];
  name?: RawButtonProps['name'];
  onClick?: RawButtonProps['onClick'];
  /* @see RawButtonProps.onMouseEnter - a `Tooltip` anchor is made interactive through these. */
  onDoubleClick?: RawButtonProps['onDoubleClick'];
  onBlur?: RawButtonProps['onBlur'];
  onFocus?: RawButtonProps['onFocus'];
  onMouseEnter?: RawButtonProps['onMouseEnter'];
  onMouseLeave?: RawButtonProps['onMouseLeave'];
  pattern?: RawButtonProps['pattern'];
  /** The desktop box; a small screen widens anything but `fixed-dimension` to a thumb target. */
  size?: IconButtonSize | `${IconButtonSize}`;
  tabIndex?: RawButtonProps['tabIndex'];
  title?: RawButtonProps['title'];
  to?: RawButtonProps['to'];
  toTab?: RawButtonProps['toTab'];
  type?: RawButtonProps['type'];
  variation?: RawButtonProps['variation'];
};

export const IconButton = ({
  title,
  className,
  disabled,
  download,
  softDisabled,
  floating,
  icon,
  id,
  inverse,
  name,
  onClick,
  onDoubleClick,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  pattern,
  size = IconButtonSize.MEDIUM,
  tabIndex,
  to,
  toTab,
  type,
  variation,
}: IconButtonProps) => {
  const ButtonIcon = <Icon name={icon} />;
  return (
    <RawButton
      className={AttrsHelper.formatClassname(
        className,
        'icon',
        // Only when it is not the default, so every existing button's class list is left alone.
        floating && 'icon-floating',
        size !== IconButtonSize.MEDIUM && `icon-size-${size}`
      )}
      disabled={disabled}
      download={download}
      softDisabled={softDisabled}
      Icon={ButtonIcon}
      id={id}
      inverse={inverse}
      name={name}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onBlur={onBlur}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      pattern={pattern}
      tabIndex={tabIndex}
      title={title}
      to={to}
      toTab={toTab}
      type={type}
      variation={variation}
    />
  );
};
